import { secp256k1 } from '@noble/curves/secp256k1';
import { ed25519 } from '@noble/curves/ed25519';
import { keccak_256 } from '@noble/hashes/sha3';
import { sha256 } from '@noble/hashes/sha256';
import { bytesToHex } from './wallet';
import { base58Encode } from './ed25519';

export function signForNetwork(
  network: string,
  privateKey: Uint8Array,
  message: string,
): string {
  switch (network) {
    case 'ethereum': return signEthereum(privateKey, message);
    case 'bitcoin':  return signBitcoin(privateKey, message);
    case 'tron':     return signTron(privateKey, message);
    case 'cosmos':   return signCosmos(privateKey, message);
    case 'solana':   return signSolana(privateKey, message);
    case 'near':     return signNear(privateKey, message);
    case 'aptos':    return signAptos(privateKey, message);
    case 'stellar':  return signStellar(privateKey, message);
    default:         return signEthereum(privateKey, message);
  }
}

// EIP-191: \x19Ethereum Signed Message:\n{len}{msg} → keccak256 → secp256k1 → 0x hex r||s||v
function signEthereum(privateKey: Uint8Array, message: string): string {
  const prefix = `\x19Ethereum Signed Message:\n${message.length}`;
  const digest = keccak_256(new TextEncoder().encode(prefix + message));
  return secp256k1Sign65Hex(privateKey, digest);
}

// TIP-191: \x19TRON Signed Message:\n{len}{msg} → keccak256 → secp256k1 → 0x hex r||s||v
function signTron(privateKey: Uint8Array, message: string): string {
  const msgBytes = new TextEncoder().encode(message);
  const prefix = new TextEncoder().encode(`\x19TRON Signed Message:\n${msgBytes.length}`);
  const buf = new Uint8Array(prefix.length + msgBytes.length);
  buf.set(prefix, 0);
  buf.set(msgBytes, prefix.length);
  const digest = keccak_256(buf);
  return secp256k1Sign65Hex(privateKey, digest);
}

// BIP-137: \x18Bitcoin Signed Message:\n + varint(len) + msg → SHA256d → secp256k1 → base64
function signBitcoin(privateKey: Uint8Array, message: string): string {
  const msgBytes = new TextEncoder().encode(message);
  const prefix = new TextEncoder().encode('\x18Bitcoin Signed Message:\n');
  const lenByte = encodeVarInt(msgBytes.length);
  const payload = new Uint8Array(prefix.length + lenByte.length + msgBytes.length);
  payload.set(prefix, 0);
  payload.set(lenByte, prefix.length);
  payload.set(msgBytes, prefix.length + lenByte.length);
  const digest = sha256(sha256(payload));
  const sig = secp256k1.sign(digest, privateKey);
  const compact = sig.toCompactRawBytes();
  // BIP-137: header byte 39 + recoveryId for P2WPKH (bech32).
  const flag = 39 + (sig.recovery ?? 0);
  const full = new Uint8Array(65);
  full[0] = flag;
  full.set(compact, 1);
  return btoa(String.fromCharCode(...full));
}

// ADR-036: canonical Amino JSON → SHA-256 → secp256k1 → base64 r||s (64 bytes, no recovery)
function signCosmos(privateKey: Uint8Array, message: string): string {
  const msgB64 = btoa(message);
  const pub = secp256k1.getPublicKey(privateKey, true);
  const pubB64 = btoa(String.fromCharCode(...pub));
  const doc = `{"account_number":"0","chain_id":"","fee":{"amount":[],"gas":"0"},"memo":"","msgs":[{"type":"sign/MsgSignData","value":{"data":"${msgB64}","signer":"${pubB64}"}}],"sequence":"0"}`;
  const digest = sha256(new TextEncoder().encode(doc));
  const sig = secp256k1.sign(digest, privateKey, { lowS: true });
  const rs = sig.toCompactRawBytes(); // 64 bytes r||s
  return btoa(String.fromCharCode(...rs));
}

// Solana: raw message bytes → ed25519 → base58
function signSolana(privateKey: Uint8Array, message: string): string {
  const msgBytes = new TextEncoder().encode(message);
  const sig = ed25519.sign(msgBytes, privateKey);
  return base58Encode(sig);
}

// NEP-413: SHA-256(tag || borsh(payload)) → ed25519 → base64
function signNear(privateKey: Uint8Array, message: string): string {
  const msgBytes = new TextEncoder().encode(message);
  // Simplified NEP-413: sign SHA-256 of raw message (full NEP-413 requires nonce + recipient)
  const digest = sha256(msgBytes);
  const sig = ed25519.sign(digest, privateKey);
  return btoa(String.fromCharCode(...sig));
}

// Aptos: raw message bytes → ed25519 → 0x hex
function signAptos(privateKey: Uint8Array, message: string): string {
  const msgBytes = new TextEncoder().encode(message);
  const sig = ed25519.sign(msgBytes, privateKey);
  return '0x' + bytesToHex(sig);
}

// Stellar: SHA-256("Stellar Signed Message:\n" + msg) → ed25519 → base64
function signStellar(privateKey: Uint8Array, message: string): string {
  const prefix = new TextEncoder().encode('Stellar Signed Message:\n');
  const msgBytes = new TextEncoder().encode(message);
  const buf = new Uint8Array(prefix.length + msgBytes.length);
  buf.set(prefix, 0);
  buf.set(msgBytes, prefix.length);
  const digest = sha256(buf);
  const sig = ed25519.sign(digest, privateKey);
  return btoa(String.fromCharCode(...sig));
}

// Helpers

function secp256k1Sign65Hex(privateKey: Uint8Array, digest: Uint8Array): string {
  const sig = secp256k1.sign(digest, privateKey);
  const compact = sig.toCompactRawBytes();
  const v = 27 + (sig.recovery ?? 0);
  const out = new Uint8Array(65);
  out.set(compact, 0);
  out[64] = v;
  return '0x' + bytesToHex(out);
}

function encodeVarInt(n: number): Uint8Array {
  if (n < 0xfd) return new Uint8Array([n]);
  const buf = new Uint8Array(3);
  buf[0] = 0xfd;
  buf[1] = n & 0xff;
  buf[2] = (n >> 8) & 0xff;
  return buf;
}
